import { useState} from 'react';
import { Today as TodayIcon, Search as SearchIcon } from '@mui/icons-material';
import { AutocompleteInput, DateInput, GlobalButton } from '@/components';
import { Option as AutocompleteOption } from '@/components/inputs/AutocompleteInput';
import { useAutocomplete } from '@/hooks';
import { useDrivers } from '@/pages/Fleet/Drivers/hooks';
import { useBusinesses } from '@/pages/Fleet/Businesses/hooks';
import { useVehicles } from '@/pages/Fleet/Vehicles/hooks';
import { Vehicle } from '@/pages/Fleet/Vehicles/models';
import SelectMultiInput from '@/components/inputs/SelectMultiInput';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton.tsx';
import { Driver } from '@/pages/Fleet/Drivers/models';
import { Business } from '@/pages/Fleet/Businesses/models';
import { AssignmentsFilter } from '@/pages/Fleet/Assignment/hooks/useAssignments';
import { DateFilter } from '../hooks/useAssignmentFilters';
import styles from './SearchBar.module.css';

import { useGetVehiclesTypesQuery } from '@/pages/Fleet/Vehicles/services/vehicles-types.service';
import { useGetServicesTypesQuery } from '../../Businesses/services/services-type.service';

const statusOptions = [
  { label: 'Pendiente', value: 'PENDING' },
  { label: 'Suspendido', value: 'SUSPENDED' },
  { label: 'En Proceso', value: 'IN_PROCESS' },
  { label: 'Finalizado', value: 'FINISHED' }
];

interface SearchBarProps {
  initialDates?: DateFilter;
  filters: AssignmentsFilter;
  changeFilters: (filters: AssignmentsFilter) => void;
  hideDriverSearch?: boolean;
  hideVehicleSearch?: boolean;
  hideStatusFilter?: boolean;
  hideVehicleTypeFilter?: boolean;
  hideServiceTypeFilter?: boolean;
}

const SearchBar = ({ initialDates, filters, changeFilters, hideDriverSearch, hideVehicleSearch, hideStatusFilter, hideVehicleTypeFilter, hideServiceTypeFilter }: SearchBarProps) => {
  const [businessesSearch, setBusinessesSearch] = useState('');
  const [driversSearch, setDriversSearch] = useState('');
  const [vehiclePlateSearch, setVehiclePlateSearch] = useState('');

  const { businesses } = useBusinesses({ search: businessesSearch, page: 1, pageSize: 10 });
  const { drivers } = useDrivers({ search: driversSearch, page: 1, pageSize: 10 });
  const { vehicles } = useVehicles({
    filters: { plate: vehiclePlateSearch || undefined },
    page: 1,
    pageSize: 10
  });

  const { options: businessesOptions } = useAutocomplete({
    data: businesses,
    getLabel: (business: Business) => business.name
  });

  const { options: driversOptions } = useAutocomplete({
    data: drivers,
    getLabel: (driver: Driver) => `${driver.firstName} ${driver.lastName}`
  });

  const { options: vehiclesOptions } = useAutocomplete({
    data: vehicles,
    getLabel: (vehicle: Vehicle) => `${vehicle.plate} - ${vehicle.description}`
  });

  const [selectedBusiness, setSelectedBusiness] = useState<AutocompleteOption | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<AutocompleteOption | null>(null);
  const [selectedVehicleByPlate, setSelectedVehicleByPlate] = useState<AutocompleteOption | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState<AutocompleteOption | null>(null);
  const [selectedServiceType, setSelectedServiceType] = useState<AutocompleteOption | null>(null);
  
  const [dateFrom, setDateFrom] = useState<Date | null>(filters.dateFrom || null);
  const [dateTo, setDateTo] = useState<Date | null>(filters.dateTo || null);

  const { data: vehicleTypes = [] } = useGetVehiclesTypesQuery();
  const { data: serviceTypes = [] } = useGetServicesTypesQuery();

  const vehicleTypeOptions = vehicleTypes.map(vehicle => ({
    label: vehicle.name,
    value: vehicle.id,
    data: vehicle
  }));

  const serviceTypeOptions = serviceTypes.map(service => ({
    label: service.name,
    value: service.id,
    data: service
  }));

  const applyFilters = () => {
    changeFilters({
      dateFrom: dateFrom ? new Date(dateFrom.setHours(0, 0, 0, 0)) : undefined,
      dateTo: dateTo ? new Date(dateTo.setHours(23, 59, 59, 999)) : undefined,
      businessId: selectedBusiness?.data.id,
      driverId: selectedDriver?.data.id,
      vehicleId: selectedVehicleByPlate?.data.id,
      status: selectedStatuses,
      vehicleTypeId: selectedVehicleType?.data.id,
      serviceTypeId: selectedServiceType?.data.id
    });
  };

  const clearFilters = () => {
    setDateFrom(initialDates?.dateFrom || null);
    setDateTo(initialDates?.dateTo || null);
    setSelectedBusiness(null);
    setSelectedDriver(null);
    setSelectedVehicleByPlate(null);
    setSelectedStatuses([]);
    setSelectedVehicleType(null);
    setSelectedServiceType(null);
    changeFilters({
      dateFrom: initialDates?.dateFrom,
      dateTo: initialDates?.dateTo,
      businessId: undefined,
      driverId: undefined,
      vehicleId: undefined,
      status: [],
      vehicleTypeId: undefined,
      serviceTypeId: undefined
    });
  };

  return (
    <>
      {initialDates && (
        <div className={styles.container}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <DateInput
              name={'assignment-date-from-search'}
              placeholder={'Desde'}
              value={dateFrom}
              onChange={setDateFrom}
            />
            <DateInput
              name={'assignment-date-to-search'}
              placeholder={'Hasta'}
              value={dateTo}
              onChange={setDateTo}
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={() => {
                setDateFrom(initialDates.dateFrom);
                setDateTo(initialDates.dateTo);
              }}
            >
              <TodayIcon color={'primary'} />
              Ver asignaciones de hoy
            </GlobalButton>
          </div>
        </div>
      )}
      <div className={styles.container}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <AutocompleteInput
            name={'assignment-filter-business-input'}
            placeholder={'Buscar por negocio'}
            value={selectedBusiness}
            onChange={setSelectedBusiness}
            onInputChange={setBusinessesSearch}
            options={businessesOptions}
          />
          {!hideDriverSearch && (
            <AutocompleteInput
              name={'assignment-filter-driver-input'}
              placeholder={'Buscar por conductor'}
              value={selectedDriver}
              onChange={setSelectedDriver}
              onInputChange={setDriversSearch}
              options={driversOptions}
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!hideVehicleSearch && (
            <AutocompleteInput
              name={'assignment-filter-vehicle-input'}
              placeholder={'Buscar por matrícula'}
              value={selectedVehicleByPlate}
              onChange={setSelectedVehicleByPlate}
              onInputChange={setVehiclePlateSearch}
              options={vehiclesOptions}
            />
          )}
          {!hideStatusFilter && (
            <SelectMultiInput
              name={'assignment-status-filter'}
              placeholder={'Filtrar por estado'}
              options={statusOptions}
              value={selectedStatuses}
              onChange={setSelectedStatuses}
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!hideVehicleTypeFilter && (
            <AutocompleteInput
              name={'assignment-vehicle-type-filter'}
              placeholder={'Filtrar por tipo de vehículo'}
              value={selectedVehicleType}
              onChange={(option) => setSelectedVehicleType(option)}
              options={vehicleTypeOptions}
              onInputChange={(input) =>
                console.log('Texto ingresado en vehículo:', input)
              }
            />
          )}
          {!hideServiceTypeFilter && (
            <AutocompleteInput
              name={'assignment-service-type-filter'}
              placeholder={'Filtrar por tipo de servicio'}
              value={selectedServiceType}
              onChange={setSelectedServiceType}
              options={serviceTypeOptions}
              onInputChange={(input) =>
                console.log('Texto ingresado en servicio:', input)
              }
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <GlobalButton
            variant={GlobalButtonVariant.primaryButton}
            action={applyFilters}
          >
            <SearchIcon /> Buscar
          </GlobalButton>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={clearFilters}
          >
            Borrar filtros
          </GlobalButton>
        </div>
      </div>
    </>
  );
};

export default SearchBar;
